import React, { useEffect, useState } from "react";
import { db } from "@/config/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { calcOCEFromGame, calcKCE } from "@/utils/scoring";

export default function Glossar() {
  const { user } = useAuth();
  const [values, setValues] = useState({ oce: 0, KCE: 0, score: 0, minutes: 0, beers: 0 });

  useEffect(() => {
    (async () => {
      if (!user) return;
      
      // Load game stats and events
      const [gsSnap, eventsSnap] = await Promise.all([
        getDocs(query(collection(db, "game_stats"), where("player_id","==", user.uid))),
        getDocs(collection(db, "events"))
      ]);
      
      const games = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const lastStat = gsSnap.docs.map(d => ({ id: d.id, ...d.data() })).slice(-1)[0];
      
      // Calculate TeamMargin for the last game
      let teamMargin = null;
      if (lastStat && lastStat.game_id) {
        const event = games.find(g => g.id === lastStat.game_id);
        if (event && event.final_home_score && event.final_away_score) {
          const homeScore = Number(event.final_home_score || 0);
          const awayScore = Number(event.final_away_score || 0);
          const scoreDiff = homeScore - awayScore;
          
          // Determine team based on plus_minus alignment
          if ((lastStat.plus_minus >= 0 && scoreDiff >= 0) || 
              (lastStat.plus_minus < 0 && scoreDiff < 0)) {
            teamMargin = scoreDiff;
          } else {
            teamMargin = -scoreDiff;
          }
        }
      }
      
      const oce = lastStat ? calcOCEFromGame(lastStat, teamMargin).OCE : 0;
      
      const dayIdPrefix = new Date().toISOString().slice(0,10);
      const kd = await getDocs(collection(db, "kiosk_day"));
      const my = kd.docs.map(d=>d.data()).find(x=>x.user_id===user.uid && x.date===dayIdPrefix);
      const minutes = my?.minutes_at_kiosk||0; 
      const beers = my?.beers_at_kiosk||0;
      const { KCE } = calcKCE({ minutes, beers });
      
      setValues({ oce, KCE, score: oce + KCE, minutes, beers });
    })();
  }, [user]);

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-xl font-bold mb-3">📘 RheinStars Glossar – Dein Legenden-Kompass</h2>

      <section className="space-y-4 text-sm">
        <div className="bg-[#160b0f] p-3 rounded-lg border border-yellow-500/30">
          <h3 className="font-semibold mb-1 text-yellow-400">🆕 OCE Version 2.0 – November 2025</h3>
          <p className="text-rose-200/90 text-xs mb-2">Neue fairere Berechnung mit Blowout-Korrektur!</p>
        </div>
        
        <div>
          <h3 className="font-semibold mb-1">🏀 OCE – On-Court Efficiency v2</h3>
          <p className="text-rose-200/80 font-semibold">Formel: OCE = (PPM × 10) × IMPACT × FT_adj × FoulAgg</p>
          <p className="text-rose-200/80 mt-2">Erklärung: OCE misst, wie groß dein tatsächlicher Einfluss auf dem Feld ist. Er verbindet Scoring-Effizienz, Team-Impact, Freiwurfleistung und kontrollierte Aggressivität zu einem Gesamtwert. Je höher dein OCE, desto stärker dein Einfluss auf Sieg oder Niederlage.</p>
          <p className="text-rose-200/60 mt-1">📊 Datengrundlage: Punkte, Spielminuten, Plus/Minus-Wert, Team-Differenz, Fouls, Freiwurfquote.</p>
        </div>
        <div>
          <h3 className="font-semibold mb-1">⚡ PPM – Points per Minute</h3>
          <p className="text-rose-200/80">Formel: PPM = Punkte / Minuten</p>
          <p className="text-rose-200/80">Erklärung: Deine erzielten Punkte pro Spielminute – dein reiner Scoring-Output. Je höher dein PPM, desto effizienter nutzt du deine Einsatzzeit.</p>
          <p className="text-rose-200/60">📊 Datengrundlage: Punkte & Einsatzzeit.</p>
        </div>
        <div>
          <h3 className="font-semibold mb-1">🧠 IMPACT – Fairer Team-Impact-Faktor</h3>
          <p className="text-rose-200/80 font-mono text-xs mb-1">
            ExpectedPerMin = TeamMargin / 40<br/>
            ExpectedPlusMinus = ExpectedPerMin × Minuten<br/>
            RelativePlusMinus = PlusMinus − ExpectedPlusMinus<br/>
            Presence = Minuten / 40<br/>
            PresenceMultiplier = 1 + 0,2 × Presence<br/>
            IMPACT = 1 + (RelativePlusMinus / 10) × PresenceMultiplier
          </p>
          <p className="text-rose-200/80">Erklärung: IMPACT zeigt, wie stark das Team mit dir auf dem Feld im Vergleich zum erwarteten Spielverlauf abschneidet. So wird dein Beitrag auch bei klaren Siegen oder Niederlagen fair eingeordnet.</p>
          <p className="text-rose-200/60">📊 Datengrundlage: Plus/Minus, Spielminuten, Team-Punkte-Differenz.</p>
        </div>
        <div>
          <h3 className="font-semibold mb-1">🎯 FT_adj – Freiwurf-Faktor</h3>
          <p className="text-rose-200/80">Formel: FT_adj = 1 + 1 × (FT% − 0,70) → min 0,80 · max 1,20</p>
          <p className="text-rose-200/80">(Keine Freiwürfe → 1,00)</p>
          <p className="text-rose-200/80">Erklärung: Die Freiwurfquote zählt jetzt richtig: 70% ist neutral. Alles darüber hebt deinen Wert, alles darunter senkt ihn – bis zu ±20%.</p>
          <p className="text-rose-200/60">📊 Datengrundlage: Freiwurfquote (FT%).</p>
        </div>
        
        <div>
          <h3 className="font-semibold mb-1">🔥 FoulAgg – Aggressivitäts-Multiplikator</h3>
          <p className="text-rose-200/80 font-mono text-xs mb-1">
            Fouls_expected = (Fouls / Minuten) × 40<br/>
            Fouls_round = gerundet auf ganze Zahl<br/>
            Mapping: 0 → 0,95 · 1 → 0,98 · 2–3 → 1,03 · 4+ → 1,10
          </p>
          <p className="text-rose-200/80">Erklärung: Fouls werden als Zeichen von Präsenz und Einsatz belohnt. 0 Fouls gelten als zu passiv, 2–3 sind optimal – und 4+ stehen für maximale Aggressivität.</p>
          <p className="text-rose-200/60">📊 Datengrundlage: Persönliche Fouls & Spielzeit.</p>
        </div>
        
        <div className="bg-[#160b0f] p-3 rounded-lg border border-blue-500/30">
          <h3 className="font-semibold mb-1 text-blue-400">💥 Endformel OCE v2</h3>
          <p className="text-rose-200/80 font-mono">OCE = (PPM × 10) × IMPACT × FT_adj × FoulAgg</p>
          <p className="text-rose-200/80 mt-2 text-xs">Ein hoher OCE bedeutet: Du punktest effizient, spielst mit Einfluss auf das Team, bist an der Linie sicher – und bringst genau die richtige Portion Härte aufs Feld.</p>
        </div>
        <div>
          <h3 className="font-semibold mb-1">🍻 KCE – Kiosk Contribution Efficiency</h3>
          <p className="text-rose-200/80">Formel: KCE = (KioskMinuten / 15) + (Biere × 0,9)</p>
          <p className="text-rose-200/80">Erklärung: Dein sozialer Beitrag nach dem Spiel – Bleiben, anstoßen, helfen. Das stärkt die Teamkultur.</p>
          <p className="text-rose-200/60">📊 Datengrundlage: Kiosk‑Präsenz in der App.</p>
        </div>
        <div>
          <h3 className="font-semibold mb-1">⭐ RheinStarsScore – Gesamtwertung</h3>
          <p className="text-rose-200/80">Formel: RheinStarsScore = OCE (letztes Spiel/Heute) + KCE (Heute)</p>
          <p className="text-rose-200/80">Erklärung: Vereint Leistung & Gemeinschaft zu deinem Gesamtwert pro Spieltag/Woche.</p>
          <p className="text-rose-200/60">📊 Datengrundlage: Spielstatistik + Kiosk‑Präsenz.</p>
        </div>
      </section>

      <section className="mt-5 text-sm">
        <h3 className="font-semibold mb-2">📅 Deine heutigen Werte</h3>
        <div className="grid gap-2">
          <div>OCE: {values.oce.toFixed(2)}</div>
          <div>KCE: {values.KCE.toFixed(2)} (Minuten: {values.minutes}, Biere: {values.beers})</div>
          <div>RheinStarsScore: {values.score.toFixed(2)}</div>
        </div>
      </section>

      <section className="mt-6 text-sm">
        <h3 className="font-semibold mb-2">📘 Kurz gesagt</h3>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#160b0f] text-rose-200/80"><tr><th className="p-2 text-left">Kürzel</th><th className="p-2 text-left">Bedeutung</th><th className="p-2 text-left">Was zeigt es?</th><th className="p-2 text-left">Quelle</th><th className="p-2 text-left">Icon</th></tr></thead>
            <tbody>
              <tr className="border-t border-white/10"><td className="p-2">OCE</td><td className="p-2">On-Court Efficiency</td><td className="p-2">Leistung auf dem Feld</td><td className="p-2">Spielstatistik</td><td className="p-2">🏀</td></tr>
              <tr className="border-t border-white/10"><td className="p-2">KCE</td><td className="p-2">Kiosk Contribution Efficiency</td><td className="p-2">Engagement am Kiosk</td><td className="p-2">Kiosk‑Präsenz</td><td className="p-2">🍻</td></tr>
              <tr className="border-t border-white/10"><td className="p-2">RheinStarsScore</td><td className="p-2">Gesamtwertung</td><td className="p-2">Leistung + Teamgeist</td><td className="p-2">Kombination</td><td className="p-2">⭐</td></tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
